import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import styles from "./message.module.scss";
import type { ComponentMessageType } from "../../types/ComponentMessageType";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { Highlighted } from "@whatslly/ui-components/Highlighted";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { SELECTORS } from "@whatslly/ui-components/Pages/TuvisMessaging/services/selectors";
import { QuotedMessage } from "./Components/QuotedMessage";
import { MessageLocation } from "./Components/Location";
import { MessageFile } from "./Components/File";
import { MessageContacts } from "./Components/Contact";
import sortBy from "lodash/sortBy";
import { useClickOutside } from "@whatslly/ui-components/componentsUtils";
import dayjs from "dayjs";
import type { MessageFloatingActionType } from "./Components/FloatingActions";
import { FloatingActions } from "./Components/FloatingActions";
import { InlineActions, MessageInlineActionType } from "./Components/InlineActions";
import { MessageAck, MessageAckFailed } from "@whatslly/ui-components/Pages/TuvisMessaging/MessageAck";
import { useImageDimentions } from "../../hooks/useImageDimentions";
import { StatusEnum } from "@whatslly/types/tuvis-messaging/TuvisMessagingEnums";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { messagesModals } from "../../stores/messagesModals";
import { RxCountdownTimer } from "react-icons/rx";
import { TbArrowForward } from "react-icons/tb";
import { FaBroadcastTower } from "react-icons/fa";
import { HiLocationMarker } from "react-icons/hi";
import { MessageMobileHandleStore } from "./MessageMobileHandleStore";

type MessageIndicatorItem = {
  icon: React.ReactNode;
  label: string;
};

const MessageTypeIndicator: React.FC<{
  message: ComponentMessageType;
}> = React.memo(({ message }) => {
  const { t } = useTranslation();

  // Collect all applicable indicators to show
  const indicators = useMemo(() => {
    const result: MessageIndicatorItem[] = [];

    if (message.isForwarded) {
      result.push({
        icon: <TbArrowForward size={12} />,
        label: t("chat_component.forwarded"),
      });
    }
    if (message.chatType === "broadcast") {
      result.push({
        icon: <FaBroadcastTower size={11} />,
        label: t("chat_component.broadcast_list"),
      });
    }
    if (message.location) {
      result.push({
        icon: <HiLocationMarker size={12} />,
        label: t("chat_component.location"),
      });
    }
    if (message.isDisappearing) {
      result.push({
        icon: <RxCountdownTimer size={12} />,
        label: t("chat_component.disappearing_message"),
      });
    }

    return result;
  }, [message.isForwarded, message.chatType, message.location, message.isDisappearing, t]);

  if (indicators.length === 0) return null;

  return (
    <div className={styles.messageMetaIndicators}>
      {indicators.map((indicator, index) => (
        <div key={index} className={styles.messageMetaIndicator}>
          {indicator.icon}
          <span>{indicator.label}</span>
        </div>
      ))}
    </div>
  );
});

const shouldContrastTime = (message: ComponentMessageType) =>
  Boolean(
    !message.msg &&
      (message.file?.fileType === "image" ||
        message.file?.fileType === "sticker" ||
        (message.file?.fileType === "video" && message.file?.previewUrl) ||
        (message.location?.preview && !message.location?.name))
  );

const EditedMessagesModal: React.FC<IModalInstance & { editedMessages: ComponentMessageType[]; crm: LoginProviderType }> = (props) => {
  const { editedMessages, crm } = props;
  const { t } = useTranslation();
  return (
    <BaseModal
      closeOnClickOutside={false}
      backdropClassName={styles.editedMessagesModalBackdrop}
      className={styles.editedModal}
      bodyClassName={styles.editedModalBody}
      closeModal={props.closeModal}
      header={<ModalHeader closeModal={props.closeModal}>{t("chat_component.message_history")}</ModalHeader>}
    >
      {editedMessages.map((message) => (
        <Message
          key={message.id}
          crm={crm}
          message={{ ...message, datetime: message.latestUpdate ?? message.datetime }}
          highlightMessage={() => {}}
          messages={[]}
          searchText={""}
        />
      ))}
    </BaseModal>
  );
};

const openEditedMessagesModal = (event: React.MouseEvent, editedMessages: ComponentMessageType[], crm: LoginProviderType) => {
  event.stopPropagation();
  event.preventDefault();
  return messagesModals.openModal(EditedMessagesModal, { editedMessages, crm });
};

export const Message: React.FC<{
  message: ComponentMessageType;
  searchText: string;
  getEntityUrl?: (type: string, id: string) => string;
  startChat?: (data: { firstName: string; lastName: string; phone: string }) => void;
  crm: LoginProviderType;
  highlightMessage: (messageId: string) => void;
  messages: ComponentMessageType[];
  floatingActions?: [MessageFloatingActionType] | [MessageFloatingActionType, MessageFloatingActionType];
  editReaction?: (messageId: string) => void;
  inlineActions?: MessageInlineActionType[];
  disableActions?: boolean;
  hideMessageAck?: boolean;
  reSendMessage?: (msgId: string | undefined) => void;
  reSendingIds?: string[];
  checkIsFraudMessage?: (msgId: string) => void;
  handleMessageUnMount?: (messageId: string) => void;
}> = observer(
  ({
    messages,
    message,
    searchText,
    getEntityUrl,
    startChat,
    crm,
    highlightMessage,
    floatingActions,
    editReaction,
    inlineActions,
    disableActions,
    hideMessageAck,
    reSendMessage,
    reSendingIds,
    checkIsFraudMessage,
    handleMessageUnMount,
  }) => {
    const [inlineActionsIsOpen, setInlineActionsIsOpen] = useState(false);
    const { t } = useTranslation();
    const messageRowRef = useRef<HTMLDivElement>(null);
    const quotedMsg = useMemo(() => {
      if (message.quotedMsgId) {
        return messages.find((i) => i.msgId === message.quotedMsgId);
      }
    }, [message.quotedMsgId, messages]);
    const isResending = useMemo(() => Boolean(message.msgId && reSendingIds?.includes(message.msgId)), [message.msgId, reSendingIds]);
    const disableAllActions = useMemo(() => disableActions || message.isTemplate, [disableActions, message.isTemplate]);
    const timeContrast = useMemo(() => shouldContrastTime(message), [message]);
    const mobileStore = useMemo(() => new MessageMobileHandleStore(), []);
    const [messageWithImageContainerStyle, messageWithImageStyle] = useImageDimentions(message.width, message.height);
    const hasMediaPreview = useMemo(
      () => message.file?.fileType === "image" || message.file?.fileType === "sticker" || message.file?.fileType === "video",
      [message.file?.fileType]
    );
    const isTemplateMediaMessage = useMemo(() => Boolean(message.isTemplate && hasMediaPreview), [hasMediaPreview, message.isTemplate]);
    const messageContentContainerStyle = useMemo(() => {
      return {
        width:
          !isTemplateMediaMessage && (message.width || message.file?.fileType === "video")
            ? `${Number(messageWithImageContainerStyle.width?.split("px")[0]) + 18}px`
            : undefined,
      };
    }, [isTemplateMediaMessage, message.width, message.file?.fileType, messageWithImageContainerStyle.width]);

    useEffect(() => {
      checkIsFraudMessage?.(message.id);
      return () => {
        handleMessageUnMount?.(message.id);
      };
    }, []);

    if (message.isInternal) {
      return (
        <div
          data-testid={`msg-row-${message.id}`}
          data-msg-id={message.msgId ?? message.id}
          className={styles.internalNoteRow}
        >
          <div className={styles.internalNote}>
            <div className={styles.internalNoteLabel}>{t("internal_note", "Internal Note")}</div>
            <div className={styles.internalNoteText}>{message.msg}</div>
            <div className={styles.internalNoteDatetime}>{dayjs(message.datetime).format("HH:mm")}</div>
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid={`msg-row-${message.id}`}
        data-msg-id={message.msgId ?? message.id}
        {...(message.isSent ? SELECTORS.MESSAGE_OUT : SELECTORS.MESSAGE_IN)}
        className={classnames(styles.messageRow, message.isSent ? styles.messageRowOut : styles.messageRowIn, {
          [styles.disableActions]: disableActions,
          [styles.templateMessage]: message.isTemplate,
        })}
        ref={messageRowRef}
        onTouchStart={mobileStore.handleMouseDown}
        onTouchEnd={mobileStore.handleMouseUp}
      >
        <Tooltip
          disabled={!message.userEmail || message.isEdited}
          value={message.userEmail}
          placement={message.isSent ? "left" : "right"}
          delay={1_000}
        >
          <div
            className={classnames(styles.messageAndReactionsContainer, { [styles.topZIndexForInlineActions]: inlineActionsIsOpen })}
            data-testid={`msg-row-content-container-data-${message.id}`}
          >
            <div className={styles.messageRowNoReactions}>
              <div
                {...SELECTORS.MESSAGE_CONTAINER}
                className={classnames(styles.message, {
                  [styles.messageOut]: message.isSent,
                  [styles.messageIn]: !message.isSent,
                  [styles.withTail]: message.withTail,
                  [styles.templateMediaMessage]: isTemplateMediaMessage,
                })}
                style={messageContentContainerStyle}
              >
                {!disableAllActions && (
                  <InlineActions
                    isOpen={inlineActionsIsOpen}
                    setIsOpen={(newState) => setInlineActionsIsOpen(newState)}
                    inlineActions={inlineActions}
                    message={message}
                    messageRowRef={messageRowRef}
                    floatingActions={floatingActions}
                    mobileStore={mobileStore}
                  />
                )}
                {!disableAllActions &&
                  ![StatusEnum.ReSent, StatusEnum.Queued, StatusEnum.Failed, StatusEnum.Deleted].includes((message.status || "") as any) && (
                    <FloatingActions message={message} floatingActions={floatingActions} />
                  )}
                {quotedMsg && (
                  <QuotedMessage
                    message={quotedMsg.msg}
                    senderName={quotedMsg.senderName}
                    timeContrast={shouldContrastTime(quotedMsg)}
                    onClick={() => highlightMessage(quotedMsg?.msgId || "")}
                    file={quotedMsg.file}
                    testid={quotedMsg.id}
                    location={quotedMsg.location}
                  />
                )}
                <MessageTypeIndicator message={message} />
                {(message.withTail || message.isTemplate) && (
                  <div className={classnames(styles.messageSenderReceiver)}>
                    <Tooltip delay={300} value={message.senderPhoneNumber} placement="top">
                      <a
                        data-whatslly-phone={message.senderPhoneNumber}
                        target="_blank"
                        href={getEntityUrl?.(message?.senderLogicName || "", message.senderId || "")}
                        className={classnames({
                          [styles.noLink]: !message?.senderLogicName || !message.senderId,
                          [styles.senderIsTuvisSystem]: message.isTemplate,
                        })}
                        style={{ color: message.nameColor }}
                        data-testid={`msg-sender-name-${message.id}`}
                      >
                        {message.senderName}
                      </a>
                    </Tooltip>
                    {message.receiverName && (
                      <>
                        <SvgV2 width={6} height={6} iconName="arrow-right-short" />
                        <Tooltip delay={300} value={message.receiverPhoneNumber} placement="top">
                          <a
                            data-whatslly-phone={message.receiverPhoneNumber}
                            target="_blank"
                            href={getEntityUrl?.(message.receiverLogicName || "", message.receiverId || "")}
                            className={classnames({ [styles.noLink]: !message.receiverLogicName || !message.receiverId })}
                          >
                            {message.receiverName}
                          </a>
                        </Tooltip>
                      </>
                    )}
                    <span className={classnames(styles.timeWithSpan, { [styles.editedSpan]: message.isEdited })}></span>
                  </div>
                )}
                {message.file && (
                  <MessageFile
                    searchText={searchText}
                    crm={crm}
                    file={message.file}
                    withTail={Boolean(message.withTail)}
                    timeContrast={timeContrast}
                    height={message.height}
                    width={message.width}
                    imgStyle={isTemplateMediaMessage ? undefined : { img: messageWithImageStyle, container: messageWithImageContainerStyle }}
                  />
                )}
                {message.msg && message.isDisappearing ? (
                  <DisappearingMessage message={message} searchText={searchText} />
                ) : (
                  <RegularMessage message={message} searchText={searchText} />
                )}

                {message.location && <MessageLocation location={message.location} testId={`${message.id}-location`} />}
                {message.contacts && <MessageContacts contacts={message.contacts} startChat={startChat} testId={`${message.id}-contact`} />}
                <div className={classnames(styles.messageSentTime, { [styles.contrast]: timeContrast })}>
                  {(message.isEdited || message.isDeleted) && message.editedMessages?.length && (
                    <span
                      data-testid={`edited-${message.id}`}
                      className={styles.messageEdited}
                      onClick={(e) => {
                        openEditedMessagesModal(e, message.editedMessages ?? [], crm);
                      }}
                    >
                      {message.isDeleted ? t("chat_component.deleted") : t("chat_component.edited")}
                    </span>
                  )}
                  {dayjs((message.isEdited || message.isDeleted) && message.latestUpdate ? message.latestUpdate : message.datetime).format("HH:mm")}
                  {message.status && message.isSent && !hideMessageAck && <MessageAck status={message.status} />}
                </div>
              </div>
              {message.status && message.isSent && !hideMessageAck && (
                <MessageAckFailed
                  status={message?.status}
                  errorCode={message.errorCode}
                  reSendMessage={() => reSendMessage?.(message.msgId)}
                  isResending={isResending}
                  hideResend={message.isTemplate || !reSendMessage || !message.msgId || message.status === StatusEnum.ReSent}
                />
              )}
            </div>
            <ReactionsComponent
              reactions={message.reactions}
              isSent={message.isSent}
              receiverPhone={message.receiverPhoneNumber}
              editReaction={editReaction}
              providerMessageId={message.msgId}
              testIdPrefix={message.id}
            />
          </div>
        </Tooltip>
      </div>
    );
  }
);

const ReactionsComponent: React.FC<{
  reactions?: ComponentMessageType["reactions"];
  isSent: boolean;
  receiverPhone: string | undefined;
  editReaction?: (messageId: string) => void;
  providerMessageId?: string;
  testIdPrefix: string;
}> = ({ reactions, isSent, receiverPhone, editReaction, providerMessageId, testIdPrefix }) => {
  const { t } = useTranslation();
  const [reactionCustomTooltipIsOpen, setReactionCustomTooltipIsOpen] = useState(false);
  const sortedReactions = useMemo(() => sortBy(reactions, (reaction) => !reaction.isSent), [reactions]);
  const reactionTooltipRef = useRef<HTMLDivElement>(null);
  const clickOutSideRef = useClickOutside<HTMLDivElement>({
    callback: () => setReactionCustomTooltipIsOpen(false),
    activate: true,
    ignoredElements: [reactionTooltipRef],
  });
  if (!reactions || reactions.length === 0) return <></>;
  return (
    <div className={styles.reactionsContainer} onClick={() => setReactionCustomTooltipIsOpen((prev) => !prev)} ref={clickOutSideRef}>
      <div className={styles.reactions}>
        {sortedReactions.map((reaction, key) => (
          <div className={styles.reaction} key={`${reaction.emoji}-${key}`} data-testid={`${testIdPrefix}-${reaction.emoji}-${key}`}>
            {reaction.emoji}
          </div>
        ))}
      </div>
      {reactionCustomTooltipIsOpen && (
        <div className={classnames(styles.reactionsCustomTooltipWrapper, { [styles.messageIsSent]: isSent })}>
          <div className={styles.reactionsCustomTooltipContainer}>
            {sortedReactions.map((reaction, key) => (
              <div
                className={classnames(styles.reactionTooltipContentWrapper, {
                  [styles.hoverEffect]: reaction.isSent && editReaction,
                  [styles.borderBottom]: reactions.length > 1 && key === 0,
                })}
              >
                <div
                  className={styles.reactionTooltipContentContainer}
                  onClick={() => {
                    if (!reaction.isSent) return;
                    if (!providerMessageId) return;
                    editReaction?.(providerMessageId);
                  }}
                >
                  <div className={styles.reactionOwner}>
                    {reaction.isSent ? t("chat_component.you") : receiverPhone || t("chat_component.customer")}
                  </div>
                  {reaction.isSent && editReaction && <div className={styles.clickToRemoveText}>{t("chat_component.click_to_remove")}</div>}
                </div>
                <div className={styles.reactionCustomTooltipEmoji}>{reaction.emoji}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const RegularMessage: React.FC<{
  message: ComponentMessageType;
  searchText: string;
}> = React.memo(({ message, searchText }) => {
  return (
    <div data-testid="element-text" className={styles.textContainer}>
      <Highlighted
        text={message.msg}
        highlight={searchText}
        className={styles.messageText}
        testid={`msg-message-${message.id}`}
        tuvisId={SELECTORS.MESSAGE_BODY}
      />
      <span className={classnames(styles.timeWithSpan, { [styles.editedSpan]: message.isEdited })}></span>
    </div>
  );
});

const DisappearingMessage: React.FC<{
  message: ComponentMessageType;
  searchText: string;
}> = React.memo(({ message, searchText }) => {
  return (
    <div className={styles.disappearingMessageContainer}>
      <Highlighted
        text={message.msg}
        highlight={searchText}
        className={styles.messageText}
        testid={`msg-message-${message.id}`}
        tuvisId={SELECTORS.MESSAGE_BODY}
      />
    </div>
  );
});
